/**
 * Per-session cache for a tenant's street-alias list, mirroring
 * `boundaryCache.ts`'s shape. Read by `MapSearchControl` (via
 * `useAddressSearchIndex`) so the alias list is fetched once per tenant
 * rather than once per search-icon click.
 */
// Legacy JS API module — typed via the permissive `@/api/*` shim, so the
// response needs a cast.
import * as streetAliasesApi from '@/api/streetAliases';
import type { StreetAliasLike } from '@/hooks/useAddressSearchIndex';

export interface StreetAliasRecord extends StreetAliasLike {
  id: number;
  tenant_id: number;
}

interface StreetAliasListResponse {
  street_aliases?: StreetAliasRecord[];
}

const cache = new Map<number, StreetAliasRecord[]>();
const inFlight = new Map<number, Promise<StreetAliasRecord[]>>();

/**
 * Fetch (or return the cached) street-alias list for a tenant.
 *
 * A failed fetch resolves to `[]` and is NOT cached, so a later caller can
 * retry — aliases are a search enhancement, not a blocker, so a failure
 * warns and search just falls back to official street names only.
 */
export function loadTenantStreetAliases(tenantId: number): Promise<StreetAliasRecord[]> {
  const cached = cache.get(tenantId);
  if (cached) return Promise.resolve(cached);

  const pending = inFlight.get(tenantId);
  if (pending) return pending;

  const promise = (streetAliasesApi.listStreetAliases({
    tenant_id: tenantId,
    page: 1,
    page_size: 200,
  }) as Promise<StreetAliasListResponse>)
    .then((data) => {
      const list = data?.street_aliases ?? [];
      cache.set(tenantId, list);
      return list;
    })
    .catch((err) => {
      console.warn('Failed to load street aliases:', err);
      return [] as StreetAliasRecord[];
    })
    .finally(() => {
      inFlight.delete(tenantId);
    });

  inFlight.set(tenantId, promise);
  return promise;
}

/** Drop the cached list for a tenant so the next load re-fetches — call after
 *  creating/editing/deleting an alias in the Streets settings tab. */
export function invalidateTenantStreetAliases(tenantId: number): void {
  cache.delete(tenantId);
}
